import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResolverPatrocinioRequest, ResolverPatrocinioResponse } from '../models/patrocinio.models';
import { EstadoInvitacion, Postulacion } from '../models/postulaciones.models';
import { PatrocinioDecisionesService } from './patrocinio-decisiones.service';

@Injectable({ providedIn: 'root' })
export class PatrocinioService {
  private readonly baseUrl = `${environment.apiBaseUrl}/patrocinios`;

  constructor(
    private readonly http: HttpClient,
    private readonly patrocinioDecisionesService: PatrocinioDecisionesService
  ) {}

  aceptarPatrocinio(idPostulacion: string, payload: ResolverPatrocinioRequest): Observable<ResolverPatrocinioResponse> {
    return this.resolverPatrocinio(idPostulacion, 'aceptada', payload);
  }

  firmarPatrocinio(idPostulacion: string, payload: ResolverPatrocinioRequest): Observable<ResolverPatrocinioResponse> {
    if (!environment.useMockApi) {
      return this.http.post<ResolverPatrocinioResponse>(`${this.baseUrl}/${idPostulacion}/firmar`, payload);
    }

    this.patrocinioDecisionesService.registrarFirma(idPostulacion, payload.rol);

    return of({
      idPostulacion,
      estadoInvitacion: 'aceptada',
      firmaPatrocinioRealizada: true,
      bloqueadaPorRechazoPatrocinio: false,
      comentario: null,
      fechaActualizacion: new Date().toISOString(),
    });
  }

  observarPatrocinio(idPostulacion: string, payload: ResolverPatrocinioRequest): Observable<ResolverPatrocinioResponse> {
    return this.resolverPatrocinio(idPostulacion, 'observada', payload);
  }

  rechazarPatrocinio(idPostulacion: string, payload: ResolverPatrocinioRequest): Observable<ResolverPatrocinioResponse> {
    return this.resolverPatrocinio(idPostulacion, 'rechazada', payload);
  }

  aplicarEstadoPatrocinio(postulaciones: Postulacion[]): Postulacion[] {
    return this.patrocinioDecisionesService.aplicarDecisiones(postulaciones);
  }

  aplicarRespuesta(postulacion: Postulacion, response: ResolverPatrocinioResponse): Postulacion {
    return {
      ...postulacion,
      estadoPostulacion: response.estadoInvitacion === 'observada' ? 'observada' : postulacion.estadoPostulacion,
      estadoInvitacion: response.estadoInvitacion,
      estadoPatrocinio:
        response.estadoInvitacion === 'aceptada'
          ? 'patrocinado'
          : response.estadoInvitacion === 'rechazada'
            ? 'sin_patrocinio'
            : postulacion.estadoPatrocinio,
      editablePorAyudante: response.bloqueadaPorRechazoPatrocinio ? false : response.estadoInvitacion === 'observada' ? true : postulacion.editablePorAyudante,
      puedeEditar: response.estadoInvitacion === 'observada' ? true : response.bloqueadaPorRechazoPatrocinio ? false : postulacion.puedeEditar,
      puedeEnviar: response.estadoInvitacion === 'observada' ? true : response.bloqueadaPorRechazoPatrocinio ? false : postulacion.puedeEnviar,
      bloqueadaPorRechazoPatrocinio: response.bloqueadaPorRechazoPatrocinio,
      firmaPatrocinioRealizada: response.firmaPatrocinioRealizada,
      ultimaActualizacion: response.fechaActualizacion,
    };
  }

  private resolverPatrocinio(
    idPostulacion: string,
    decision: Extract<EstadoInvitacion, 'aceptada' | 'rechazada' | 'observada'>,
    payload: ResolverPatrocinioRequest
  ): Observable<ResolverPatrocinioResponse> {
    if (!environment.useMockApi) {
      return this.http.post<ResolverPatrocinioResponse>(`${this.baseUrl}/${idPostulacion}/${this.segmentoDecision(decision)}`, payload);
    }

    const comentario = payload.comentario?.trim() || undefined;
    this.patrocinioDecisionesService.registrarDecision(idPostulacion, decision, payload.rol, comentario, false);

    return of({
      idPostulacion,
      estadoInvitacion: decision,
      firmaPatrocinioRealizada: false,
      bloqueadaPorRechazoPatrocinio: decision === 'rechazada',
      comentario: comentario ?? null,
      fechaActualizacion: new Date().toISOString(),
    });
  }

  private segmentoDecision(decision: Extract<EstadoInvitacion, 'aceptada' | 'rechazada' | 'observada'>): string {
    if (decision === 'aceptada') return 'aceptar';
    if (decision === 'rechazada') return 'rechazar';
    return 'observar';
  }
}
